'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does Trident Gum Have Microplastics? Yes — It's in the Gum Base (2026)"
      author="MicroPlastics Research Desk"
      date="July 15, 2026"
      readTime="9 min read"
      heroImage="/blog/heroes/food.webp"
      heroImageAlt="Does Trident gum have microplastics — the synthetic gum base audited against the 2025 UCLA saliva study"
    >
      <BlogLastUpdated iso="2026-07-15" formatted="July 15, 2026" />

      <BlogTLDR>
        <strong>Yes. Trident&apos;s &ldquo;gum base&rdquo; is synthetic plastic,
        and chewing it sheds microplastics into your saliva.</strong>{' '}
        Like most mainstream gum, Trident (Mondelēz) is built on a{' '}
        <strong>synthetic gum base</strong> — a petroleum-derived blend of polymers
        such as polyvinyl acetate and polyethylene, legally hidden under the single
        words &ldquo;gum base&rdquo; with none of its up-to-46 permitted ingredients
        individually disclosed. A 2025 UCLA pilot study found that chewing gum
        releases roughly <strong>100&ndash;600 microplastic particles per gram</strong>,
        so a single piece can shed hundreds into your mouth, most within the first
        eight minutes. The honest twist: the same study found <strong>natural
        chicle gums shed similar amounts</strong>, so switching brands cuts the
        petroleum-plastic <em>ingredient</em> but is not proven to stop the shedding.
        The only guaranteed reduction is chewing less.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Trident uses a <strong>synthetic gum base</strong> — petroleum-derived polymers (polyvinyl acetate, polyethylene, and others) listed only as &ldquo;gum base.&rdquo;</>,
          <>&ldquo;Gum base&rdquo; is a legal umbrella for up to <strong>46 FDA-permitted ingredients</strong>, none of which must be named individually on the label.</>,
          <>A 2025 UCLA study found chewing gum releases <strong>~100&ndash;600 microplastic particles per gram</strong>; a typical piece is 2&ndash;6 grams, and 94% shed in the first 8 minutes.</>,
          <><strong>Natural and synthetic gums shed similar particle counts</strong> in that study — so &ldquo;plastic-free&rdquo; gum is a cleaner ingredient list, not a proven zero.</>,
          <>Chewing 160&ndash;180 pieces a year could mean ingesting <strong>tens of thousands of microplastic particles</strong> from gum alone.</>,
          <>Lower-exposure options: <Link href="/blog/does-simply-gum-have-microplastics" className="text-forest underline">chicle-based gum</Link>, or simply chewing less. See the full <Link href="/blog/chewing-gum-brands-ranked-microplastics" className="text-forest underline">gum brands ranked</Link>.</>,
        ]}
      />

      <RiskQuizInlineNudge source="blog_inline_trident" />

      <KeyStats
        heading="Trident & microplastics — the facts"
        stats={[
          { value: 'Synthetic', label: 'Trident gum base', context: 'petroleum-derived polymers (PVA, polyethylene) under "gum base"', source: 'Chewing gum ingredient labeling', sourceUrl: 'https://www.acs.org/pressroom/presspacs/2025/march/chewing-gum-can-shed-microplastics-into-saliva-pilot-study-finds.html' },
          { value: '100–600/g', label: 'microplastics released per gram', context: '2025 UCLA pilot study across 10 gum brands', source: 'Mohanty / UCLA, ACS Spring 2025', sourceUrl: 'https://newsroom.ucla.edu/releases/bursting-your-bubble-chewing-gum-releases-microplastics-into-your-saliva-ucla-research-shows' },
          { value: '94%', label: 'shed in first 8 minutes', context: 'most particles release early in the chew', source: 'UCLA 2025 pilot study', sourceUrl: 'https://www.acs.org/pressroom/presspacs/2025/march/chewing-gum-can-shed-microplastics-into-saliva-pilot-study-finds.html' },
          { value: '46', label: 'ingredients hidden by "gum base"', context: 'FDA-permitted additives that need no individual disclosure', source: 'FDA gum-base labeling', sourceUrl: 'https://www.fda.gov/food' },
        ]}
      />

      <h2>Does Trident gum contain microplastics?</h2>
      <p>
        Yes. The chewy part of Trident is a <strong>synthetic gum base</strong>, and
        &ldquo;synthetic&rdquo; here means plastic. Modern mainstream gum bases are
        blends of food-grade polymers — commonly polyvinyl acetate (the same polymer
        family as white glue), polyethylene, and synthetic rubbers like
        styrene-butadiene and butyl rubber. On the label, all of it collapses into two
        words: <strong>&ldquo;gum base.&rdquo;</strong> US rules let manufacturers use
        that umbrella for up to 46 permitted ingredients without naming any of them, so
        you can&apos;t tell from the package exactly which polymers your Trident
        contains.
      </p>
      <p>
        In March 2025, a UCLA team led by Sanjay Mohanty presented a pilot study (at
        the American Chemical Society spring meeting) that chewed 10 gum brands and
        measured what ended up in saliva. The result: gum releases on the order of{' '}
        <strong>100 to 600 microplastic particles per gram</strong>, and since a
        typical piece weighs 2&ndash;6 grams, a single piece can shed several hundred
        particles — with 94% released in the first eight minutes of chewing.
      </p>

      <h2>The honest catch: natural gum shed similar amounts</h2>
      <p>
        Here is the part most &ldquo;switch to natural gum&rdquo; articles skip. The
        UCLA study tested five synthetic gums <em>and</em> five natural (chicle-based)
        gums, and found they released <strong>similar levels of microplastic
        particles.</strong> That does not make Trident&apos;s synthetic base a
        non-issue — it is still petroleum-derived plastic you are chewing — but it does
        mean that swapping to a &ldquo;plastic-free&rdquo; chicle gum gives you a
        cleaner <em>ingredient list</em>, not a guaranteed end to shedding. The one
        change that reliably cuts your gum-derived microplastics is chewing fewer
        pieces, or for less time.
      </p>

      <h2>What to chew instead</h2>
      <BlogComparisonTable
        caption="Trident vs lower-exposure gum options"
        columns={[
          { key: 'option', label: 'Option' },
          { key: 'base', label: 'Gum base' },
          { key: 'note', label: 'Note' },
        ]}
        rows={[
          { option: 'Trident / Orbit / Extra / 5', base: 'Synthetic (petroleum polymers)', note: 'Plastic gum base; sheds microplastics' },
          { option: 'Chicle gum (Simply Gum, Glee, Chicza)', base: 'Chicle (tree sap)', note: 'Plant base — cleaner ingredients, but still sheds particles' },
          { option: 'Chew less / shorter', base: 'n/a', note: 'The only change proven to cut gum-derived microplastics' },
          { option: 'Mints or crunchy veg for fresh breath', base: 'None', note: 'No gum base at all' },
        ]}
      />
      <p>
        Compare brands in our{' '}
        <Link href="/blog/chewing-gum-brands-ranked-microplastics" className="text-forest underline">gum brands ranked for microplastics</Link>{' '}
        and the{' '}
        <Link href="/blog/chewing-gum-brands-ranked-microplastics" className="text-forest underline">microplastics in chewing gum</Link>{' '}
        explainer, or read the{' '}
        <Link href="/blog/does-orbit-gum-have-microplastics" className="text-forest underline">Orbit</Link>{' '}
        and{' '}
        <Link href="/blog/does-simply-gum-have-microplastics" className="text-forest underline">Simply Gum</Link>{' '}
        verdicts.
      </p>

      <BlogAppCTA
        headline="Gum is one hidden plastic source — find the rest"
        sub="The app can't scan a stick of gum, but it can scan the packaged foods, drinks, and containers around it. Build a picture of where plastic actually enters your day with the MicroPlastics app."
      />

      <BlogFAQ
        items={[
          {
            question: 'Is Trident gum made of plastic?',
            answer: 'Partly, yes. Trident\'s "gum base" is a synthetic, petroleum-derived blend of polymers such as polyvinyl acetate and polyethylene. It is food-grade, but it is plastic, and it is what gives the gum its chew.',
          },
          {
            question: 'How many microplastics does chewing Trident release?',
            answer: 'A 2025 UCLA pilot study found chewing gum releases roughly 100 to 600 microplastic particles per gram of gum into saliva. A typical piece weighs 2 to 6 grams, so a single piece can shed several hundred particles, with 94% released in the first eight minutes.',
          },
          {
            question: 'Is natural gum better than Trident for microplastics?',
            answer: 'Natural chicle gum has a cleaner, plant-based ingredient list with no petroleum polymers. However, the UCLA study found natural and synthetic gums shed similar amounts of microplastics, so switching brands reduces the plastic ingredient but is not proven to stop the shedding. Chewing less is the reliable reduction.',
          },
          {
            question: 'Do you swallow the microplastics from gum?',
            answer: 'The particles are released into saliva as you chew, and swallowed saliva carries some of them into your digestive tract. Most release early in the chew, so a shorter chew and fewer pieces reduce the amount ingested.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'Mohanty S, et al. (UCLA)', year: 2025, title: 'Chewing Gums: Unintended sources of ingested microplastics in humans (ACS Spring 2025)', publisher: 'American Chemical Society', url: 'https://www.acs.org/pressroom/presspacs/2025/march/chewing-gum-can-shed-microplastics-into-saliva-pilot-study-finds.html' },
          { authors: 'UCLA Newsroom', year: 2025, title: 'Chewing gum releases microplastics into your saliva, UCLA research shows', publisher: 'UCLA', url: 'https://newsroom.ucla.edu/releases/bursting-your-bubble-chewing-gum-releases-microplastics-into-your-saliva-ucla-research-shows' },
          { authors: 'World Health Organization', year: 2022, title: 'Dietary and inhalation exposure to nano- and microplastic particles', publisher: 'WHO', url: 'https://www.who.int/publications/i/item/9789240054608' },
        ]}
      />
    </BlogPostLayout>
  )
}
